import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, useTheme } from '@mui/material';
import { getLocalStorageItem, hasRole } from '../../../Services/Global';
import { Roles } from '../../../Models/Enums';

// Define a NavLink interface to replace INavLink
interface NavLink {
  name: string;
  url: string;
  isExpanded: boolean;
}

/**
 * AdminNavigation component for the admin settings page navigation bar using Material UI
 */
export const AdminNavigation: React.FC<{
  updateSelection: (key: string) => void
}> = ({ updateSelection }) => {
  const theme = useTheme();
  
  // Build navigation links based on user roles
  const buildNavLinks = (): NavLink[] => {
    let navLinks: NavLink[] = [
      {
        name: 'Settings',
        url: 'Settings',
        isExpanded: true,
      }
    ];

    if (hasRole(...[Roles[Roles.Modeler]])) {
      navLinks.push(
        {
          name: 'Unit configuration',
          url: 'UnitConfigurationManagement',
          isExpanded: true,
        },
        {
          name: 'PHE Plate type',
          url: 'PHEPlateTypeManagement',
          isExpanded: false,
        }
      );
    }

    if (hasRole(...[Roles[Roles.SME]])) {
      navLinks.push(
        {
          name: 'SME design guideline',
          url: 'SMEDesignGuideline',
          isExpanded: false,
        }
      );
    }

    return navLinks;
  };

  const navLinks = buildNavLinks();
  
  // Always default to Settings if no valid tab is found
  const getInitialTab = () => {
    const saved = getLocalStorageItem('admin:currentSelection');
    if (saved && navLinks.some(link => link.url === saved)) {
      return saved;
    }
    return 'Settings';
  };
  
  const [activeTab, setActiveTab] = useState<string>(getInitialTab());

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue) {
      setActiveTab(newValue);
      updateSelection(newValue);
    }
  };

  // Initialize activeTab when component mounts
  useEffect(() => {
    const initialTab = getInitialTab();
    setActiveTab(initialTab);
    updateSelection(initialTab);
  }, []);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: { xs: 'flex-start', md: 'center' },
      p: { xs: 2, md: '16px 24px' },
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      mb: 2
    }}>
      <Typography 
        variant="h5" 
        sx={{ 
          mr: { md: 4 }, 
          mb: { xs: 2, md: 0 },
          fontWeight: 'medium',
          color: theme.palette.primary.main,
          ml: 2
        }}
      >
        Administration
      </Typography>
      
      <Tabs 
        value={activeTab || 'Settings'} 
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        aria-label="Admin navigation tabs"
        sx={{
          '& .MuiTabs-indicator': {
            height: 3,
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3
          },
          minHeight: 48
        }}
      >
        {navLinks.map((link, index) => (
          <Tab 
            key={index} 
            label={link.name} 
            value={link.url}
            sx={{ 
              textTransform: 'none',
              fontWeight: 'medium',
              fontSize: '0.95rem',
              minHeight: 48,
              px: 3
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}; 